import React from 'react';
import { useAdmin } from '../../contexts/AdminContext';
import { 
  DocumentTextIcon, 
  PhotoIcon, 
  NewspaperIcon,
  EyeIcon,
  ExclamationTriangleIcon
} from '@heroicons/react/24/outline';

const AdminDashboard = () => {
  const { siteContent, blogPosts, layoutConfig, resetToDefaults } = useAdmin();

  const stats = [
    {
      name: 'Content Sections',
      value: Object.keys(siteContent).length,
      icon: DocumentTextIcon,
      color: 'text-blue-600'
    },
    {
      name: 'Blog Posts',
      value: blogPosts?.length || 3,
      icon: NewspaperIcon,
      color: 'text-green-600'
    },
    {
      name: 'Layout Elements',
      value: Object.keys(layoutConfig).length,
      icon: EyeIcon,
      color: 'text-purple-600'
    },
    {
      name: 'Images',
      value: Object.keys(siteContent).length,
      icon: PhotoIcon,
      color: 'text-orange-600'
    }
  ];

  const quickActions = [
    { name: 'Edit Hero Text', href: '/admin/content#hero', section: 'hero' },
    { name: 'Add Blog Post', href: '/admin/blog/create', section: 'blog' },
    { name: 'Upload Images', href: '/admin/images', section: 'images' },
    { name: 'Toggle Sections', href: '/admin/layout', section: 'layout' }
  ];

  return (
    <div className="section-padding">
      <div className="container-custom">
        <div className="max-w-6xl mx-auto">
          <div className="mb-8">
            <h1 className="text-3xl font-bold text-naqd-light-text dark:text-naqd-dark-text mb-2">
              Admin Dashboard
            </h1>
            <p className="text-naqd-light-text-secondary dark:text-naqd-dark-text-secondary">
              Manage your website content, images, and layout settings
            </p>
          </div>

          {/* Stats */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
            {stats.map((stat) => (
              <div key={stat.name} className="card">
                <div className="flex items-center">
                  <div className={`p-2 rounded-lg ${stat.color} bg-opacity-10`}>
                    <stat.icon className={`w-6 h-6 ${stat.color}`} />
                  </div>
                  <div className="ml-4">
                    <p className="text-sm font-medium text-naqd-light-text-secondary dark:text-naqd-dark-text-secondary">
                      {stat.name}
                    </p>
                    <p className="text-2xl font-bold text-naqd-light-text dark:text-naqd-dark-text">
                      {stat.value}
                    </p>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Quick Actions */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
            <div className="card">
              <h3 className="text-lg font-semibold mb-4 text-naqd-light-text dark:text-naqd-dark-text">
                Quick Actions
              </h3>
              <div className="space-y-2">
                {quickActions.map((action) => (
                  <a
                    key={action.name}
                    href={action.href}
                    className="block p-3 rounded-lg bg-naqd-light-surface dark:bg-naqd-dark-surface hover:bg-naqd-light-surface/80 dark:hover:bg-naqd-dark-surface/80 transition-colors"
                  >
                    {action.name}
                  </a>
                ))}
              </div>
            </div>

            <div className="card">
              <h3 className="text-lg font-semibold mb-4 text-naqd-light-text dark:text-naqd-dark-text">
                Recent Changes
              </h3>
              <div className="space-y-2 text-sm text-naqd-light-text-secondary dark:text-naqd-dark-text-secondary">
                <p>• Content updated: Hero section</p>
                <p>• New blog post: "Latest GST Updates"</p>
                <p>• Images uploaded: Team photos</p>
                <p>• Layout: Contact form enabled</p>
              </div>
            </div>
          </div>

          {/* Reset Button */}
          <div className="card border-l-4 border-red-500">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="text-lg font-semibold text-naqd-light-text dark:text-naqd-dark-text mb-2 flex items-center">
                  <ExclamationTriangleIcon className="w-5 h-5 text-red-500 mr-2" />
                  Reset All Settings
                </h3>
                <p className="text-naqd-light-text-secondary dark:text-naqd-dark-text-secondary">
                  This will restore all content to default values and remove all custom changes.
                </p>
              </div>
              <button
                onClick={resetToDefaults}
                className="px-4 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600 transition-colors"
              >
                Reset All
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminDashboard;